import type { HarborInstanceConfig } from "@p0security/integrations-harbor-shared/types";
import type { FrontendInstallContext } from "@p0security/integrations-installer";
import { Typography } from "@p0security/ui";
import { useEffect, useState } from "react";

const { Paragraph, Text } = Typography;

/** What the instance's connector reports of it, as far as the installer
 * shows it. Each is there only when P0's answer held it as text. */
type InstanceFacts = {
  /** The connector's own setting for whose email may receive access */
  allowedEmailDomainPattern?: string;
  harborVersion?: string;
};

/** The states at which an instance's connector has checked it, so that P0
 * has facts to answer with. */
const CHECKED_STATES: readonly unknown[] = ["configure", "installed"];

const textOf = (value: unknown) =>
  typeof value === "string" && value !== "" ? value : undefined;

/** The facts in `body`, P0's answer to a read of the instance. The answer
 * comes from outside this page, so nothing about its shape is assumed. */
const readFacts = (body: unknown): InstanceFacts => {
  const metadata =
    typeof body === "object" && body !== null && "metadata" in body
      ? body.metadata
      : undefined;
  if (typeof metadata !== "object" || metadata === null) return {};
  const facts: Readonly<Record<string, unknown>> = { ...metadata };
  return {
    allowedEmailDomainPattern: textOf(facts.allowedEmailDomainPattern),
    harborVersion: textOf(facts.harborVersion),
  };
};

/** Shows what the connector reports of the Harbor instance `id`: Harbor's
 * version and the email domains the connector serves.
 *
 * P0 keeps none of these, so they are read with the item each time. Nothing
 * renders until the read resolves, or when it fails or holds no facts: the
 * install can go on without them, and what is wrong is the verify step's to
 * say.
 */
const InstanceFactsPanel = ({
  authFetch,
  id,
}: Pick<FrontendInstallContext<unknown>, "authFetch"> & { id: string }) => {
  const [facts, setFacts] = useState<InstanceFacts>({});

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<InstanceFacts> => {
      try {
        const response = await authFetch(
          `integrations/harbor/config/harbor-instance/${encodeURIComponent(id)}`,
          { method: "GET" }
        );
        return readFacts(await response?.json());
      } catch {
        return {};
      }
    };
    void load().then((read) => {
      if (!cancelled) setFacts(read);
    });
    return () => {
      cancelled = true;
    };
  }, [authFetch, id]);

  const { allowedEmailDomainPattern, harborVersion } = facts;
  if (!allowedEmailDomainPattern && !harborVersion) return undefined;
  return (
    <div>
      {harborVersion ? (
        <Paragraph>
          Harbor version: <Text code>{harborVersion}</Text>
        </Paragraph>
      ) : undefined}
      {allowedEmailDomainPattern ? (
        <Paragraph>
          This instance&apos;s connector gives access only to people whose email
          domain matches <Text code>{allowedEmailDomainPattern}</Text>. The
          pattern is set in the connector&apos;s Terraform, and P0 can&apos;t
          change it.
        </Paragraph>
      ) : undefined}
    </div>
  );
};

/** The instance page's preamble: what the connector reports of the instance,
 * once it has checked it. */
export const instancePreamble = (
  context: Pick<FrontendInstallContext<unknown>, "authFetch">,
  id: string,
  item: Readonly<Partial<Pick<HarborInstanceConfig, "state">>> | undefined
) =>
  CHECKED_STATES.includes(item?.state) ? (
    <InstanceFactsPanel authFetch={context.authFetch} id={id} />
  ) : undefined;
